import type { Locale } from './caseModel.js';
import type { StorySnapshot } from './source.js';

export type CastMember = 'patch' | 'bit';
export type Gesture = 'urgent' | 'think' | 'encourage' | 'concern' | 'relief';
export interface CastLine {
  readonly speaker: CastMember;
  readonly gesture: Gesture;
  readonly text: string;
}
type ScriptLine = readonly [CastMember, Gesture, string, string, string];
const line = (entry: ScriptLine, locale: Locale): CastLine => ({
  speaker: entry[0],
  gesture: entry[1],
  text: entry[locale === 'fr-FR' ? 2 : locale === 'zh-CN' ? 4 : 3],
});

// Performance copy only. Evidence, allowed actions and outcomes still come
// from StorySource. These lines never grant clues or evaluate the player.
const SCENES: Record<string, readonly ScriptLine[]> = {
  'triage-console': [
    [
      'patch',
      'urgent',
      'Tu es là ! Ce message menace de couper le nœud. J’ai la main sur le lien… Tu regardes avec moi ?',
      'You’re here! This message threatens to cut off the node. My hand is on the link… Will you check it with me?',
      '你来了！这条消息威胁要切断节点。我正要点链接……你能和我一起看看吗？',
    ],
    [
      'bit',
      'think',
      'Patch, attends. Un message peut mentir. Toi, tu prends la console : qu’est-ce qu’on peut vérifier sans lui faire confiance ?',
      'Wait, Patch. A message can lie. You take the console: what can we check without trusting it?',
      '等一下，Patch。消息可能是假的。你来操作控制台：不相信这条消息，我们还能核实什么？',
    ],
  ],
  'interpretation-checkpoint': [
    [
      'patch',
      'concern',
      'J’étais prêt à croire cette alerte. Avec ce que tu as trouvé… je ne sais plus. Qu’est-ce que tu en penses ?',
      'I was ready to believe that alert. After what you found… I’m not so sure. What do you think?',
      '我差点就相信那条警报了。看了你找到的线索……我不确定了。你怎么看？',
    ],
    [
      'bit',
      'think',
      'Je te laisse conclure. Ton explication doit tenir face à toutes les preuves, même celle qui te dérange.',
      'Your call. Your explanation needs to fit every piece of evidence, including the inconvenient one.',
      '由你判断。你的解释必须符合所有证据，包括最难解释的那一条。',
    ],
  ],
  'interpretation-recovery': [
    [
      'bit',
      'concern',
      'Attends. Cette explication laisse une preuve de côté. On peut reprendre ensemble : laquelle ne colle pas ?',
      'Wait. That explanation leaves some evidence out. Let’s look again together: what doesn’t fit?',
      '等等。这个解释漏掉了一条证据。我们再看一遍：哪里对不上？',
    ],
  ],
  'hypothesis-test': [
    [
      'patch',
      'urgent',
      'On tient une piste. Je bloque tout ?',
      'We have a lead. Should I block everything?',
      '我们有线索了。要全部封锁吗？',
    ],
    [
      'bit',
      'think',
      'Pas encore. Essaie de montrer que notre hypothèse est fausse. Si elle tient malgré ça, on pourra agir.',
      'Not yet. Try to prove our hypothesis wrong. If it survives that, we can act.',
      '还不行。试着推翻我们的假设。如果它经得住检验，我们再行动。',
    ],
  ],
  'containment-choice': [
    [
      'patch',
      'urgent',
      'Le piège est confirmé. Dis-moi comment on protège les autres. Je suis prêt.',
      'The trap is confirmed. Tell me how we protect everyone else. I’m ready.',
      '陷阱已经确认。告诉我怎么保护其他人。我准备好了。',
    ],
    [
      'bit',
      'think',
      'Et demain, il faudra expliquer ce qui s’est passé. Pense aussi aux traces que ton action va laisser.',
      'And tomorrow, we’ll need to explain what happened. Think about what your action leaves behind.',
      '明天我们还需要解释发生了什么。也想想你的操作会留下什么记录。',
    ],
  ],
  'evidence-recovery': [
    [
      'bit',
      'concern',
      'J’ai retenu la purge. Les originaux sont encore là. À toi de reprendre la main ; cette tentative restera dans le rapport.',
      'I stopped the purge. The originals are still here. Take over again; this attempt stays in the report.',
      '我拦住了清除操作，原始记录还在。请重新接手；这次尝试会留在报告里。',
    ],
  ],
  'verification-console': [
    [
      'patch',
      'think',
      'L’ordre de blocage est parti. Je voudrais souffler… mais comment on sait qu’il a marché ?',
      'The block request is sent. I’d like to relax… but how do we know it worked?',
      '封锁请求已发送。我想松口气……但怎么确认它生效了？',
    ],
    [
      'bit',
      'think',
      'C’est toi qui signes le rapport. Vérifie le résultat, puis assure-toi que les autres ont une information fiable.',
      'You’re signing the report. Check the result, then make sure everyone has reliable information.',
      '报告由你签署。先核实结果，再确保其他人得到可靠的信息。',
    ],
  ],
  'ending-solved': [
    [
      'patch',
      'relief',
      'Je peux retirer ma main de ce lien. Tu ne m’as pas juste dit « ne clique pas » : tu m’as montré pourquoi.',
      'I can take my hand off that link. You didn’t just tell me not to click. You showed me why.',
      '我可以把手从链接上移开了。你不仅告诉我不要点，还让我明白了原因。',
    ],
    [
      'bit',
      'relief',
      'Preuves conservées, résultat vérifié. Ton rapport tient debout. Beau travail, partenaire.',
      'Evidence preserved, result verified. Your report holds up. Good work, partner.',
      '证据已保存，结果已核实。你的报告站得住脚。干得好，搭档。',
    ],
  ],
  'ending-partial': [
    [
      'patch',
      'concern',
      'On a agi, mais je ne suis pas rassuré. Il reste quelque chose qu’on n’a pas terminé.',
      'We acted, but I’m still uneasy. We left something unfinished.',
      '我们采取了行动，但我还是不放心。还有事情没做完。',
    ],
    [
      'bit',
      'think',
      'Le rapport montre ce qui manque. Relis-le avec moi avant de retenter l’affaire.',
      'The report shows what’s missing. Read it with me before another attempt.',
      '报告说明了缺失的环节。再次尝试前，和我一起看看吧。',
    ],
  ],
  'ending-incorrect': [
    [
      'patch',
      'concern',
      'J’avais envie d’une explication rassurante. On a peut-être voulu y croire trop vite.',
      'I wanted a reassuring explanation. Maybe we were too quick to believe it.',
      '我想要一个让人安心的解释。也许我们太急着相信了。',
    ],
    [
      'bit',
      'think',
      'Reprenons les faits du rapport. Une conclusion doit expliquer les indices, pas les faire disparaître.',
      'Let’s revisit the report. A conclusion needs to explain the evidence, not wish it away.',
      '重新看看报告里的事实。结论需要解释证据，而不是忽略它。',
    ],
  ],
};

const RESPONSES: Record<string, ScriptLine> = {
  'inspect-alert': [
    'patch',
    'concern',
    'Une phrase de récupération… J’avais surtout regardé le compteur. Tu as bien fait d’ouvrir le message.',
    'A recovery phrase… I was only looking at the timer. Good thing you inspected the message.',
    '恢复短语……我刚才只顾看倒计时了。幸好你检查了消息。',
  ],
  'compare-domain': [
    'bit',
    'think',
    'Presque le même nom, pas la même adresse. Tu viens de vérifier l’identité au lieu de te fier à l’apparence.',
    'Almost the same name, a different address. You checked identity instead of trusting appearances.',
    '名字很像，地址却不同。你核实了身份，而不是只看外表。',
  ],
  'check-feed': [
    'patch',
    'think',
    'Le canal officiel ne dit rien ? Alors le message et l’opérateur ne racontent pas la même histoire.',
    'Nothing on the official channel? Then the message and the operator tell different stories.',
    '官方频道没有通知？那消息和运营方的说法就对不上。',
  ],
  'ask-mira': [
    'patch',
    'think',
    'Mira aussi ? Elle n’administre même pas de nœud. Garde son témoignage avec les autres indices.',
    'Mira too? She doesn’t even run a node. Keep her account with the other clues.',
    'Mira也收到了？她根本不管理节点。把她的证词和其他线索放在一起。',
  ],
  'inspect-telemetry': [
    'bit',
    'think',
    'Le nœud fonctionne. C’est un fait utile, pas encore une identité d’expéditeur. Continuons à recouper.',
    'The node works. Useful evidence, but it doesn’t identify the sender. Keep cross-checking.',
    '节点正常。这是有用的证据，但还不能确认发件人。继续交叉核实吧。',
  ],
};

export function sceneDialogue(snapshot: StorySnapshot, locale: Locale): CastLine[] {
  return (SCENES[snapshot.scene.id] ?? []).map((entry) => line(entry, locale));
}

export function actionDialogue(choiceId: string, locale: Locale): CastLine[] {
  const response = RESPONSES[choiceId];
  return response === undefined ? [] : [line(response, locale)];
}

/** Consultation uses only information already exposed to this client. */
export function consultation(
  snapshot: StorySnapshot,
  speaker: CastMember,
  locale: Locale,
): CastLine {
  if (snapshot.isEnding) {
    return (
      sceneDialogue(snapshot, locale).find((entry) => entry.speaker === speaker) ??
      line(
        [
          'bit',
          'think',
          'Relisons le rapport ensemble.',
          'Let’s read the report together.',
          '一起看看报告吧。',
        ],
        locale,
      )
    );
  }
  if (speaker === 'patch') {
    return line(
      snapshot.evidence.count === 0
        ? [
            'patch',
            'concern',
            'Je veux régler ça vite, mais je n’ai encore rien vérifié. Tu m’aides à prendre du recul ?',
            'I want this fixed fast, but I haven’t checked anything yet. Help me take a step back?',
            '我想赶快解决，但还什么都没核实。帮我冷静分析一下好吗？',
          ]
        : [
            'patch',
            'encourage',
            'Tes vérifications me font ralentir. Je reste avec toi. Quelle observation te fait le plus hésiter ?',
            'Your checks are slowing me down. I’m with you. Which observation makes you hesitate most?',
            '你的核实让我慢了下来。我会陪着你。哪条观察最让你犹豫？',
          ],
      locale,
    );
  }
  if (snapshot.scene.id === 'verification-console' || snapshot.scene.id === 'containment-choice') {
    return line(
      [
        'bit',
        'think',
        'Décris le résultat que tu veux obtenir. Quelle observation permettrait ensuite de prouver qu’il est atteint ?',
        'Describe the result you want. What could you observe afterwards to prove you achieved it?',
        '说说你想达到什么结果。之后观察什么才能证明它已经实现？',
      ],
      locale,
    );
  }
  return line(
    [
      'bit',
      'think',
      'Sépare ce que le message affirme de ce que tu as vérifié. Si ton idée était fausse, quel indice le montrerait ?',
      'Separate the message’s claims from what you verified. If your idea were wrong, what evidence would show it?',
      '把消息的声称和你核实的事实分开。如果你的想法有误，什么证据会证明这一点？',
    ],
    locale,
  );
}
